/**
 * SelectableCard - Reusable single-select card component.
 *
 * Used in onboarding multi-step flow for clickable option selection.
 * Selected state: primary border + accent background + check icon.
 */

import React from 'react';
import { cn } from '@/lib/utils';

interface SelectableCardProps {
  label: string;
  selected: boolean;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
}

const SelectableCard: React.FC<SelectableCardProps> = ({
  label,
  selected,
  onClick,
  disabled = false,
  className,
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'relative w-full text-left rounded-xl border-2 px-4 py-3 transition-all duration-200',
        'hover:border-primary/50 hover:shadow-sm',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50',
        selected
          ? 'border-primary bg-primary/5 shadow-sm'
          : 'border-border bg-card',
        disabled && 'opacity-50 cursor-not-allowed',
        className,
      )}
    >
      <div className="flex items-center gap-3">
        {/* Radio indicator */}
        <div className={cn(
          'flex-shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors duration-200',
          selected ? 'border-primary' : 'border-muted-foreground/30',
        )}>
          {selected && (
            <div className="w-2 h-2 rounded-full bg-primary" />
          )}
        </div>
        <span className={cn(
          'text-sm font-medium',
          selected ? 'text-primary' : 'text-foreground',
        )}>
          {label}
        </span>
      </div>
    </button>
  );
};

export default SelectableCard;
